// ---------------------------------------------------------------------------
// The five-stage product spine (LANDING_INSPIRATION G1/G2): 01 Chat, 02
// Memory, 03 Meetings, 04 Agents, 05 Views. ONE registry drives every surface
// that names a stage - the Product nav popover, the /product hub, each stage
// page's sibling cross-link row, and the footer's Product column - so a page
// can never be described differently in two places.
//
// Integrations is the un-numbered sixth popover cell (a directory page, not a
// stage); Desktop keeps its URL and lives in the Resources popover.
// ---------------------------------------------------------------------------

export interface ProductStage {
	/** 1-based position in the spine. */
	index: number;
	/** Two-digit ledger number ("01"). Section numbering derives `${index}.x`. */
	number: string;
	/** Registry slug; the href is always `/product/<slug>`. */
	slug: string;
	/** One-word-ish stage name ("Chat"). */
	label: string;
	/** The benefit line: white two-liner in the popover, the cross-link row copy. */
	benefit: string;
	/**
	 * THE stage headline - the one sentence that names the stage everywhere a
	 * surface leads with it (landing ledger title, next-stage nav band). Single
	 * source: never restate this string in a page or section data file.
	 */
	headline: string;
}

export const PRODUCT_STAGES: readonly ProductStage[] = [
	{
		index: 1,
		number: '01',
		slug: 'chat',
		label: 'Chat',
		headline: 'Team Chat.',
		benefit: 'The chat your team knows, where every message becomes memory.',
	},
	{
		index: 2,
		number: '02',
		slug: 'memory',
		label: 'Memory',
		headline: 'Memory that keeps itself current.',
		benefit: 'Everything said becomes a record anyone on the team can search.',
	},
	{
		index: 3,
		number: '03',
		slug: 'meetings',
		label: 'Meetings',
		headline: 'Meetings you never have to recap.',
		benefit: 'A recording becomes decisions, owners, and next steps.',
	},
	{
		index: 4,
		number: '04',
		slug: 'agents',
		label: 'Agents',
		headline: 'Agents that run the recurring jobs.',
		benefit: 'Teammates that own routines and run on schedules.',
	},
	{
		index: 5,
		number: '05',
		slug: 'views',
		label: 'Views',
		headline: 'Notes, timelines, boards - done for you.',
		benefit: 'Boards, timelines, and notes built from the same team-shared memory.',
	},
] as const;

export const stageHref = (stage: ProductStage): string => `/product/${stage.slug}`;

/** The un-numbered sixth Product-popover cell (directory page, not a stage). */
export const INTEGRATIONS_CELL = {
	label: 'Integrations',
	benefit: 'Keep your tools. Naumu learns from them anyway.',
	href: '/product/integrations',
} as const;
